import React, { useState, useEffect } from "react";
import {
  useOutletContext,
  useNavigate,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import {
  Button,
  SkeletonPlaceholder,
  ToastNotification,
  Tile,
  Tag,
} from "@carbon/react";
import fuel from "../../../../assets/icons/fuel.svg";
import { ReactComponent as FluffyChat } from "../../../../assets/icons/fluffyChatIcon.svg";
import { useTranslation } from "react-i18next";
import CreateSubagentModal from "../../../../components/createSubagentModal/CreateSubagentModal";
import ChatRelationsDataTable from "../../../../components/chatRelationsDataTable/ChatRelationsDataTable";
import AddNewClientByUUID from "../../../../components/addNewClientByUUID/AddNewClientByUUID";
import { Slide, ToastContainer, toast } from "react-toastify";
import { createCurrencyFormatter } from "../../../../utils/currencyFormatter";
import { getRoute } from "../../../../store/routeStorage";

export default function AgentMainPage() {
  const { chatUrl, isInfoPending, responseInfo } =
    useOutletContext();
  const [openSubagentCreationModal, setOpenSubagentCreationModal] =
    useState(false);
  const [openAddNewClientModal, setOpenAddNewClientModal] = useState(false);

  const { t, i18n } = useTranslation();
  const formatter = createCurrencyFormatter(i18n.language);
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();

  const showSuccessToast = (title, subtitle) => {
    const toastId = toast(
      ({ closeToast }) => (
        <ToastNotification
          kind="success"
          title={title}
          subtitle={subtitle}
          hideCloseButton
          statusIconDescription=""
          className="toast_notification"
          aria-label={t("main.modalText.close")}
        />
      ),
      {
        containerId: "admin_notifications",
        autoClose: false,
        closeButton: false,
        pauseOnHover: false,
        pauseOnFocusLoss: false,
      },
    );

    setTimeout(() => {
      toast.dismiss(toastId);
    }, 3000);
  };

  useEffect(() => {
    const lastRoute = getRoute("admin");

    if (location.state?.skipRedirect) return;
    if (navigationType === "POP") return;

    if (lastRoute && location.pathname === "/") {
      navigate(lastRoute.pathname, {
        state: lastRoute.state,
        replace: true,
      });
    }
  }, [location.pathname]);

  return (
    <>
      {isInfoPending ? (
        <SkeletonPlaceholder />
      ) : (
        <>
          <Tile id="tile-1" className="admin">
            <div className="admin_info">
              <div className="admin_info_balance">
                {!responseInfo.uplinkAgent ? (
                  <div className="admin_info_balance_header">
                    <span>
                      {t("main.admin.main.publicName")}{" "}
                      {responseInfo.publicName || "-"}
                    </span>
                    <span>
                      {t("main.admin.main.totalBalance")} {responseInfo.email}
                    </span>
                    <h1
                      className={
                        responseInfo.totalBalance < 0 ? "negative" : ""
                      }
                    >
                      {formatter.format(responseInfo.totalBalance || 0)}
                    </h1>
                  </div>
                ) : (
                  <div className="admin_info_balance_header">
                    <span>
                      {t("main.admin.main.publicName")}{" "}
                      {responseInfo.publicName || "-"}
                    </span>
                    <span>
                      {t("main.admin.main.totalFuelBalance")}{" "}
                      {responseInfo.email}
                    </span>
                    <h1
                      className={
                        responseInfo.fuel_balance < 0 ? "negative" : ""
                      }
                    >
                      <img
                        src={fuel}
                        alt=""
                        className="admin_info_balance_fuel_img"
                      />
                      {`${responseInfo.fuel_balance || 0}`}
                    </h1>
                  </div>
                )}
              </div>
              {!isInfoPending &&
                !responseInfo.uplinkAgent &&
                responseInfo.couponDiscount != null && (
                  <Tile className="admin_info_extra-info" lang={i18n.language}>
                    <Tag size="lg" type="green">
                      {`${responseInfo.couponDiscount * 100}%`}
                    </Tag>
                    <p>{t("main.admin.main.couponDiscount")}</p>
                  </Tile>
                )}
            </div>
            <div className="admin_buttons">
              <Button renderIcon={FluffyChat} href={chatUrl} target="_blank">
                {t("main.chat.chatButton")}
              </Button>
            </div>
          </Tile>
        </>
      )}
      <ChatRelationsDataTable
        pending={isInfoPending}
        relations={responseInfo.clients}
        showSuccessToast={showSuccessToast}
        setOpenSubagentCreationModal={setOpenSubagentCreationModal}
        setOpenAddNewClientModal={setOpenAddNewClientModal}
      />

      <CreateSubagentModal
        openModal={openSubagentCreationModal}
        setOpenModal={setOpenSubagentCreationModal}
        onSuccess={() =>
          showSuccessToast(
            t("authentication.notification.success.title"),
            t("main.admin.create_subagent.created"),
          )
        }
      />
      <AddNewClientByUUID
        openModal={openAddNewClientModal}
        setOpenModal={setOpenAddNewClientModal}
      />
      <ToastContainer
        containerId="admin_notifications"
        position="bottom-right"
        transition={Slide}
        icon={false}
        hideProgressBar
        pauseOnHover={false}
        pauseOnFocusLoss={false}
      />
    </>
  );
}
